import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useSidebarOpenState } from '../app/layout/SidebarOpenStateContext';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Badge from '../components/Badge';
import Button from '../components/Button';
import Buttons from '../components/Buttons';
import ContextualHelp from '../components/ContextualHelp';
import { MenuItem, MenuSeparator } from '../components/Menu';
import { BaseModalContainer } from '../components/ModalContainer';
import ModalPrimitive from '../components/ModalPrimitive';
import OverflowMenu from '../components/OverflowMenu';
import SVGIcon from '../components/SVGIcon';
import Select from '../components/Select';
import SelectOption from '../components/SelectOption';
import Slot from '../components/Slot';
import Tooltip from '../components/Tooltip';
import TextualControl from '../components/formInternals/TextualControl';
import { TopicDetached, UserType } from '../graphql/generated/graphql';
import copyIcon from '../icons/non-prefilled/16x16/copy.svg';
import hideIcon from '../icons/non-prefilled/16x16/hide.svg';
import informationIcon from '../icons/non-prefilled/16x16/information.svg';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import readIcon from '../icons/non-prefilled/16x16/read.svg';
import retryIcon from '../icons/non-prefilled/16x16/retry.svg';
import trashIcon from '../icons/non-prefilled/16x16/trash.svg';
import { useAllSlimPostMap } from '../post/useAllSlimPosts';
import useLayoutSize, { LayoutSize } from '../post/utils/useLayoutSize';
import type { EntitySelectConfig } from '../search/entitySearch/CategorizedEntitySelect';
import CategorizedEntitySelect from '../search/entitySearch/CategorizedEntitySelect';
import { miscToasts } from '../toasts/toasts';
import { useAllTopicMap } from '../topic/useAllTopics';
import { UIActions } from '../ui/uiSlice';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import Link from '../utils/router/Link';
import useCanScrollUp from '../utils/useCanScrollUp';
import usePreventLeaving from '../utils/usePreventLeaving';
import useShortcut from '../utils/useShortcut';
import SectionChangesConfirmationModal from './SectionChangesConfirmationModal';
import SectionItemList from './SectionItemList';
import SectionList from './SectionList';
import SidebarIconPicker, { COLORS } from './SidebarIconPicker';
import SidebarSectionIcon, { SIDEBAR_ICONS } from './SidebarSectionIcon';
import * as styles from './SidebarSectionsModal.module.scss';
import type { Section } from './SidebarSectionsProvider';
import {
  PremadeSections,
  canEditSection,
  getSidebarSectionNameWithFallback,
  isSectionDeleted,
  isSectionNew,
  useSidebarSectionsContext,
} from './SidebarSectionsProvider';
import useCanManageSidebarSections from './useCanManageSidebarSections';
import useExternalPostPlatforms from './useExternalPostPlatforms';

const SIDEBAR_WIDTH = parseInt(styles.sidebarWidth, 10);

interface GetPremadeSectionTooltipsArgs {
  isAdmin: boolean;
}

const getPremadeSectionTooltips = ({
  isAdmin,
}: GetPremadeSectionTooltipsArgs): Record<string, string> => ({
  [PremadeSections.ALL_TOPICS]: `lists all top level topics visible to your team.${isAdmin ? ' Reordering will also reorder for teammates.' : ''}`,
  [PremadeSections.MY_TOPICS]:
    'lists the topics that you are a member of. You can reorder them to your liking.',
  [PremadeSections.INTEGRATIONS]:
    'automatically lists post syncing sources enabled for your team.',
});

const SECTION_NAME_LENGTH_LIMIT = 255;

interface ScrollablePaneProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  innerRef?: React.RefObject<HTMLDivElement | null>;
}

const ScrollablePane = ({
  children,
  header,
  innerRef,
}: ScrollablePaneProps) => {
  const { setScrollingContainer, canScrollUp } = useCanScrollUp();

  return (
    <div className={styles.scrollablePane}>
      {!!header && (
        <div
          className={classNames(styles.actionContainer, {
            [styles.active]: canScrollUp,
          })}
        >
          {header}
        </div>
      )}
      <Slot ref={setScrollingContainer}>
        <div ref={innerRef} className={styles.innerContainer}>
          {children}
        </div>
      </Slot>
    </div>
  );
};

interface SectionItemsSelectProps {
  section: Section;
  onAddItem: (item: Section['items'][number]) => void;
}

const SectionItemsSelect = ({
  section,
  onAddItem,
}: SectionItemsSelectProps) => {
  const [inputState, setInputState] = useState({ value: '', isOpen: false });
  const setIsOpen = (isOpen: boolean) =>
    setInputState((state) => ({ ...state, isOpen }));

  const config = useMemo<EntitySelectConfig>(() => {
    const itemIds = section.items.map((item) => item.id);
    const filterItems = ({ id }: { id: string }) => !itemIds.includes(id);
    return {
      template: {
        filter: filterItems,
      },
      post: {
        filter: (post) => filterItems(post) && post.archivedAt == null,
      },
      topic: {
        filter: filterItems,
      },
      order: ['topic', 'template', 'post'] as const,
    };
  }, [section]);

  return (
    <CategorizedEntitySelect
      controlShouldRenderValue={false}
      menuIsOpen={inputState.isOpen}
      onMenuOpen={() => setIsOpen(true)}
      onMenuClose={() => setIsOpen(false)}
      inputValue={inputState.value}
      onInputChange={(value) => {
        setInputState({ value, isOpen: !!value });
      }}
      onChange={(value) => {
        if (!value) return;
        const item =
          value.type === 'topic'
            ? ({
                __typename: 'Topic',
                id: value.id,
                detached: TopicDetached.None,
              } as const)
            : ({
                __typename: 'Post',
                id: value.id,
              } as const);

        onAddItem(item);
      }}
      placeholder="Type to find posts or topics"
      config={config}
      value={null}
      menuPlacement="bottom"
      openMenuOnClick={false}
    />
  );
};

const useSectionItems = (section: Section) => {
  const allSlimPostsMap = useAllSlimPostMap();
  const allTopicsMap = useAllTopicMap();
  const platforms = useExternalPostPlatforms();
  const { myTopics, allTopics } = useSidebarSectionsContext();

  if (section.id === PremadeSections.ALL_TOPICS) {
    return allTopics;
  }

  if (section.id === PremadeSections.MY_TOPICS && myTopics) {
    return myTopics;
  }

  if (section.id === PremadeSections.INTEGRATIONS) {
    return platforms;
  }

  return _.compact(
    section.items.map((item) => {
      if (item.__typename === 'Post') {
        return allSlimPostsMap[item.id];
      }
      if (item.__typename === 'Topic') {
        return allTopicsMap[item.id];
      }
      return null;
    }),
  );
};

interface SectionPaneProps {
  section: Section;
  onDeleteSection: () => void;
  onDuplicateSection: () => void;
  onRestoreSection: () => void;
}

const SectionPane = ({
  section,
  onDeleteSection,
  onDuplicateSection,
  onRestoreSection,
}: SectionPaneProps) => {
  const boundaryRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

  const {
    permissions,
    updateSection,
    moveItemInSection,
    addItemToSection,
    removeItemFromSection,
    sidebarSections,
    toggleSectionVisibility,
  } = useSidebarSectionsContext();
  const items = useSectionItems(section);
  const isVisible = sidebarSections.some(({ id }) => id === section.id);

  const isAdmin = useAppSelector(
    (state) => state.session.user.type === UserType.Admin,
  );

  const sectionInfoTooltip = section.premade
    ? getPremadeSectionTooltips({ isAdmin })[section.id]
    : null;

  const isDeleted = isSectionDeleted(section);

  const canEdit =
    canEditSection(section.id, {
      isTeamSection: section.isTeamSection,
      isAdminUser: isAdmin,
    }) && !isDeleted;

  const canReorder =
    canEdit ||
    section.id === PremadeSections.MY_TOPICS ||
    (section.id === PremadeSections.ALL_TOPICS && isAdmin);

  return (
    <ScrollablePane innerRef={boundaryRef}>
      <>
        <div className={styles.controls}>
          <SidebarIconPicker
            boundaryRef={boundaryRef}
            onSelectIcon={(icon) => {
              updateSection({ id: section.id, icon: icon.name });
            }}
            onChangeColor={(color) => {
              updateSection({ id: section.id, color });
            }}
            currentColor={section.color ?? undefined}
            currentIcon={section.icon ?? undefined}
            disabled={!canEdit}
          >
            <SidebarSectionIcon section={section} />
          </SidebarIconPicker>
          <div className={styles.nameField}>
            <TextualControl
              disabled={!canEdit}
              value={section.name}
              placeholder={getSidebarSectionNameWithFallback('')}
              maxLength={SECTION_NAME_LENGTH_LIMIT}
              onChange={(event) => {
                updateSection({
                  id: section.id,
                  name: event.target.value,
                });
              }}
              noMinWidth
            />
          </div>
          {isDeleted ? (
            <Tooltip content="Restore deleted section" asChild>
              <Button
                className={styles.overflowMenuButton}
                icon={retryIcon}
                onClick={onRestoreSection}
              />
            </Tooltip>
          ) : section.premade ? (
            <>
              {sectionInfoTooltip && (
                <div className={styles.infoIconContainer}>
                  <ContextualHelp
                    content={{
                      paragraph: (
                        <div>
                          <strong>{section.name}</strong> {sectionInfoTooltip}
                        </div>
                      ),
                    }}
                    size={16}
                  />
                </div>
              )}
            </>
          ) : !permissions.canHideSections ? (
            <Button
              className={classNames(
                styles.overflowMenuButton,
                styles.deleteSectionButton,
              )}
              aria-label="Delete section"
              icon={trashIcon}
              onClick={onDeleteSection}
            />
          ) : (
            <OverflowMenu
              button={
                <Button
                  className={styles.overflowMenuButton}
                  icon={overflowMenuIcon}
                  aria-label="More options"
                />
              }
              open={isOverflowMenuOpen}
              onOpenChange={setIsOverflowMenuOpen}
              placement="bottom-start"
            >
              <MenuItem
                wrap
                icon={isVisible ? hideIcon : readIcon}
                onClick={() => {
                  toggleSectionVisibility(section.id);
                }}
              >
                {isVisible ? 'Hide in sidebar' : 'Show in sidebar'}
              </MenuItem>
              <MenuItem wrap icon={copyIcon} onClick={onDuplicateSection}>
                Duplicate section
              </MenuItem>
              {canEdit && (
                <>
                  <MenuSeparator />
                  <MenuItem
                    wrap
                    icon={trashIcon}
                    danger
                    onClick={onDeleteSection}
                  >
                    Delete section
                  </MenuItem>
                </>
              )}
            </OverflowMenu>
          )}
        </div>
        {permissions.canSeeTeamSections && (
          <div className={styles.sectionTypeSelect}>
            <Select
              aria-label="Section type"
              components={{ Option: SelectOption }}
              name="isTeamSection"
              value={section.isTeamSection ? 'team' : 'personal'}
              isDisabled={!canEdit}
              options={[
                {
                  label: 'Personal',
                  value: 'personal',
                  icon: '',
                  helpText: 'Only you can add this to your sidebar',
                },
                {
                  label: 'Team',
                  value: 'team',
                  icon: '',
                  helpText:
                    'Managed by admins but any user can add to their sidebar',
                  isDisabled: !permissions.canEditTeamSections,
                },
              ]}
              onChange={(value) =>
                updateSection({
                  id: section.id,
                  isTeamSection: value === 'team',
                })
              }
              isSearchable={false}
            />
          </div>
        )}
        {canEdit ? (
          <div className={styles.addItemContainer}>
            <SectionItemsSelect
              section={section}
              onAddItem={(item) => addItemToSection(section.id, item)}
            />
          </div>
        ) : null}
        <SectionItemList
          disabled={!canReorder}
          onMove={(from, to) => moveItemInSection(section.id, from, to)}
        >
          {items.map((item, index) => (
            <SectionItemList.Row
              key={item.id}
              item={item}
              index={index}
              onDeleteClick={
                canEdit
                  ? () => removeItemFromSection(section.id, item.id)
                  : undefined
              }
            />
          ))}
        </SectionItemList>
      </>
    </ScrollablePane>
  );
};

export const SIDEBAR_SECTIONS_CLICK_EVENTS = new WeakSet<MouseEvent>();

interface InnerSidebarSectionsModalProps {
  requestClose: () => void;
  requestSave: () => void;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const InnerSidebarSectionsModal = ({
  requestClose,
  requestSave,
}: InnerSidebarSectionsModalProps) => {
  const {
    permissions,
    allSections,
    sidebarSections,
    toggleSectionVisibility,
    addSection,
    deleteSection,
    restoreSection,
    moveSection,
    unsavedSections,
  } = useSidebarSectionsContext();
  const isAdmin = useAppSelector(
    (state) => state.session.user.type === UserType.Admin,
  );

  const dispatch = useAppDispatch();
  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  const selectedSection = allSections.find(
    (section) => section.id === selectedSectionId,
  );

  const titleId = useId();

  return (
    <ClickOutsideHandler
      asChild
      onClickOutside={(event) => {
        if (
          event instanceof MouseEvent &&
          !SIDEBAR_SECTIONS_CLICK_EVENTS.has(event)
        ) {
          requestClose();
        }
      }}
    >
      <ModalPrimitive.Root
        aria-labelledby={titleId}
        className={styles.container}
      >
        <ModalPrimitive.Header
          titleId={titleId}
          className={styles.header}
          close={requestClose}
          title="Sidebar sections"
          titleExtra={
            <Link newTab to="https://go.slab.com/help-sidebar-sections">
              <Badge icon={<SVGIcon size={12}>{informationIcon}</SVGIcon>}>
                Info
              </Badge>
            </Link>
          }
        />
        <div className={styles.body}>
          <ScrollablePane
            header={
              <div className={styles.createSectionButtonContainer}>
                <Button
                  className={styles.createSectionButton}
                  variant="secondary"
                  onClick={() => {
                    const section = addSection({
                      name: 'New section',
                      icon: _.sample(SIDEBAR_ICONS)?.name,
                      color: _.sample(COLORS),
                    });
                    dispatch(UIActions.setSelectedSectionId(section.id));
                  }}
                >
                  Create new section
                </Button>
              </div>
            }
          >
            <SectionList onMove={moveSection}>
              {allSections.map((section, index) => {
                return (
                  <SectionList.Row
                    section={section}
                    visible={sidebarSections.some(
                      ({ id }) => id === section.id,
                    )}
                    selected={selectedSectionId === section.id}
                    teamSection={section.isTeamSection}
                    deleted={isSectionDeleted(section)}
                    index={index}
                    key={section.id}
                    onVisibilityButtonClick={
                      permissions.canHideSections
                        ? () => toggleSectionVisibility(section.id)
                        : undefined
                    }
                    onClick={() =>
                      dispatch(UIActions.setSelectedSectionId(section.id))
                    }
                    onRestoreButtonClick={() => {
                      restoreSection(section.id);
                    }}
                  />
                );
              })}
            </SectionList>
          </ScrollablePane>
          {selectedSection && (
            <SectionPane
              section={selectedSection}
              onDeleteSection={() => {
                deleteSection(selectedSection.id);
                if (isSectionNew(selectedSection)) {
                  dispatch(
                    UIActions.setSelectedSectionId(
                      allSections.find(
                        (section) => section.id !== selectedSection.id,
                      )?.id ?? null,
                    ),
                  );
                }
              }}
              onDuplicateSection={() => {
                const duplicatedSection = addSection({
                  ...selectedSection,
                  isTeamSection: selectedSection.isTeamSection && isAdmin,
                });
                dispatch(UIActions.setSelectedSectionId(duplicatedSection.id));
              }}
              onRestoreSection={() => {
                restoreSection(selectedSection.id);
              }}
            />
          )}
        </div>
        <ModalPrimitive.Footer
          className={styles.footer}
          left={
            <div className={styles.text}>
              Changes are not live until you save
            </div>
          }
          right={
            <Buttons>
              {unsavedSections.length > 0 && (
                <ModalPrimitive.Button
                  variant="secondary"
                  onClick={requestClose}
                >
                  Discard
                </ModalPrimitive.Button>
              )}
              <ModalPrimitive.Button
                variant="primary"
                disabled={unsavedSections.length === 0}
                className={styles.saveButton}
                onClick={requestSave}
              >
                Finalize and save
              </ModalPrimitive.Button>
            </Buttons>
          }
        />
      </ModalPrimitive.Root>
    </ClickOutsideHandler>
  );
};

const SidebarSectionsModal = () => {
  const { unsavedSections, saveSections } = useSidebarSectionsContext();
  const [confirmationDialog, setConfirmationDialog] = useState<{
    type: React.ComponentProps<typeof SectionChangesConfirmationModal>['type'];
    isSaving: boolean;
  } | null>(null);

  const dispatch = useAppDispatch();
  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  if (selectedSectionId == null && confirmationDialog) {
    setConfirmationDialog(null);
  }

  usePreventLeaving({
    skip: selectedSectionId == null || unsavedSections.length === 0,
  });

  const requestClose = () => {
    if (unsavedSections.length > 0) {
      setConfirmationDialog({ type: 'DISCARD', isSaving: false });
    } else {
      dispatch(UIActions.setSelectedSectionId(null));
    }
  };

  useShortcut(
    'esc',
    (e) => {
      e.preventDefault();
      requestClose();
    },
    { global: true, skip: selectedSectionId == null },
  );

  const layoutSize = useLayoutSize();

  const canManageSidebarSections = useCanManageSidebarSections();
  useEffect(() => {
    if (!canManageSidebarSections) {
      dispatch(UIActions.setSelectedSectionId(null));
      setConfirmationDialog(null);
    }
  }, [dispatch, canManageSidebarSections]);

  const { isSidebarOpen } = useSidebarOpenState();
  const isNextToSidebar =
    isSidebarOpen &&
    layoutSize !== LayoutSize.mobile &&
    layoutSize !== LayoutSize.tablet;

  return (
    <BaseModalContainer
      open={selectedSectionId != null}
      marginLeft={isNextToSidebar ? SIDEBAR_WIDTH : undefined}
    >
      {confirmationDialog ? (
        <SectionChangesConfirmationModal
          type={confirmationDialog.type}
          saving={confirmationDialog.isSaving}
          unsavedSections={unsavedSections}
          onBack={() => setConfirmationDialog(null)}
          onConfirm={async () => {
            if (confirmationDialog?.type === 'SAVE') {
              try {
                setConfirmationDialog({ type: 'SAVE', isSaving: true });
                await saveSections();
              } catch {
                dispatch(UIActions.pushToast(miscToasts.genericError));
                setConfirmationDialog({ type: 'SAVE', isSaving: false });
                return;
              }
            }
            setConfirmationDialog(null);
            dispatch(UIActions.setSelectedSectionId(null));
          }}
        />
      ) : (
        <InnerSidebarSectionsModal
          requestClose={requestClose}
          requestSave={() => {
            setConfirmationDialog({ type: 'SAVE', isSaving: false });
          }}
        />
      )}
    </BaseModalContainer>
  );
};

export default SidebarSectionsModal;
